import {
  Cell,
  CustomModalLayout,
  Dropdown,
  FieldSet,
  Input,
  Layout,
  Modal,
  NumberInput,
  Thumbnail,
} from '@wix/design-system';
import { MediaUploadErrorKey } from '@wix/ricos/internal';
import React, { useState } from 'react';

type ModeFormState =
  | { type: 'manager' }
  | { type: 'upload'; failWith: MediaUploadErrorKey | undefined; delay: number };

const MODE_MODAL_STATE_KEY = 'MODE_MODAL_STATE';

export const MockModal: React.FC<{
  close: () => void;
  onConfirm: (args: { selected: number[]; mode: ModeFormState }) => void;
  items: { label: string; imageUrl?: string }[];
  multiple?: boolean;
}> = ({ close, onConfirm, items, multiple }) => {
  const [selected, setSelected] = useState<number[]>([0]);

  const [modeFormState, _setModeFormState] = useState<ModeFormState>(
    (window[MODE_MODAL_STATE_KEY] as any) ?? { type: 'manager' }
  );
  const setModeFormState = (s: ModeFormState) => {
    (window[MODE_MODAL_STATE_KEY] as any) = s;
    _setModeFormState(s);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      const element = document.querySelector('[data-hook="mock-modal-item-0"] [tabindex]');
      if (element) {
        (element as HTMLElement).focus();
        clearInterval(interval);
      }
    }, 50);
  }, []);

  return (
    <Modal isOpen onRequestClose={close} zIndex={100000}>
      <CustomModalLayout
        dataHook="mock-modal"
        primaryButtonText="Confirm"
        primaryButtonOnClick={() => {
          if (selected.length > 0) {
            onConfirm({ selected, mode: modeFormState });
            close();
          }
        }}
        secondaryButtonText="Cancel"
        secondaryButtonOnClick={close}
        onCloseButtonClick={close}
        title="File Upload Mock"
        subtitle={'Choose a file to "upload"'}
        sideActions={<ModeForm state={modeFormState} setState={setModeFormState} />}
        content={
          <div
            style={{
              display: 'flex',
              gap: '10px',
              padding: '10px',
              flexWrap: 'wrap',
            }}
          >
            {items.map((item, index) => {
              return (
                <Thumbnail
                  dataHook={`mock-modal-item-${index}`}
                  key={index}
                  title={item.label}
                  selected={selected.includes(index)}
                  onClick={e => {
                    if (
                      selected.includes(index) &&
                      'key' in e &&
                      e.type === 'keydown' &&
                      e.key === 'Enter'
                    ) {
                      if (e.ctrlKey && e.shiftKey) {
                        onConfirm({
                          selected,
                          mode: { type: 'upload', failWith: 1, delay: 500 },
                        });
                      } else if (e.ctrlKey) {
                        onConfirm({
                          selected,
                          mode: { type: 'upload', failWith: undefined, delay: 3000 },
                        });
                      } else {
                        onConfirm({ selected, mode: { type: 'manager' } });
                      }

                      close();
                    }
                    if (multiple) {
                      setSelected(s =>
                        s.includes(index) ? s.filter(i => i !== index) : [...s, index]
                      );
                    } else {
                      setSelected([index]);
                    }
                  }}
                  image={item.imageUrl ?? defaultImgUrl}
                  contentAlignment="top"
                  width={165}
                  height={200}
                />
              );
            })}
          </div>
        }
      />
    </Modal>
  );
};

const ModeForm = (props: { state: ModeFormState; setState: (s: ModeFormState) => void }) => {
  return (
    <Layout>
      <Cell span={4}>
        <FieldSet legend="Selection mode" legendPlacement="left">
          <Dropdown
            selectedId={props.state.type}
            size="small"
            popoverProps={{ appendTo: 'parent', zIndex: 100000 + 1, placement: 'top' }}
            onSelect={option => {
              if (option.id === 'manager') {
                props.setState({ type: 'manager' });
              } else if (option.id === 'upload') {
                props.setState({ type: 'upload', failWith: undefined, delay: 3000 });
              }
            }}
            options={[
              { id: 'manager', value: 'Media manager' },
              { id: 'upload', value: 'File Upload' },
            ]}
          />
        </FieldSet>
      </Cell>
      {props.state.type === 'upload' && (
        <>
          <Cell span={4}>
            <FieldSet legend="Delay" legendPlacement="left">
              <NumberInput
                suffix={<Input.Affix>s</Input.Affix>}
                size="small"
                value={props.state.delay / 1000}
                onChange={v =>
                  props.state.type === 'upload' &&
                  props.setState({ ...props.state, delay: (v ?? 0) * 1000 })
                }
              />
            </FieldSet>
          </Cell>
          <Cell span={4}>
            <FieldSet legend="Fail with error" legendPlacement="left">
              <Dropdown
                selectedId={props.state.failWith}
                popoverProps={{ appendTo: 'window', zIndex: 100000 + 1 }}
                options={Object.keys(MediaUploadErrorKey).map(key => ({
                  id: MediaUploadErrorKey[key],
                  value: key,
                }))}
                onSelect={option => {
                  props.state.type === 'upload' &&
                    props.setState({ ...props.state, failWith: Number(option.id) });
                }}
                placeholder="None"
                clearButton
                onClear={() =>
                  props.state.type === 'upload' &&
                  props.setState({ ...props.state, failWith: undefined })
                }
              />
            </FieldSet>
          </Cell>
        </>
      )}
    </Layout>
  );
};

const defaultImgUrl =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='164' height='120' viewBox='0 0 164 120'%3E%3Cg fill='none'%3E%3Crect width='164' height='120' fill='%23DAEFFE'/%3E%3Cpath fill='%23B9E3FF' d='M-8.52651283e-14,120 L49.0917544,74.9616932 C52.151039,72.1550101 56.848961,72.1550101 59.9082456,74.9616932 L71.293,85.4057534 L96.9019846,59.8591624 C100.0299,56.7386931 105.095216,56.744729 108.215685,59.8726439 C108.284098,59.9412201 108.35126,60.0110332 108.417137,60.0820486 L164,120 L-8.52651283e-14,120 Z'/%3E%3Ccircle cx='67.5' cy='43.5' r='10.5' fill='%23F5FBFF'/%3E%3C/g%3E%3C/svg%3E%0A";
